import React, { useMemo, useCallback } from 'react'
import type { AppStructureWithFeatures, Experiments } from '@wix/thunderbolt-symbols'
import { TemplateItemContext } from '@wix/thunderbolt-symbols'
import type { RendererProps } from '../types'
import type { RenderComp } from '../components/StructureComponent'
import type { ElementData } from '@wix/thunderbolt-becky-types'
import { COMP_PATH_DELIMITER } from '@wix/thunderbolt-becky-types'
import { getInflatedItemId, REF_DELIMITER } from '@wix/thunderbolt-commons'
import { getChildScope, getSlotsScope, emptyScope } from '../components/scopesApi'
import type { ScopeData } from '../components/scopesApi'
import { mapValues, cloneDeep, isPlainObject } from 'lodash'

interface RenderProps {
	children: Array<React.ReactNode>
	backgroundLayer: Array<React.ReactNode>
	pinnedLayer: Array<React.ReactNode>
}

export type ElementToContainersData = Record<
	string,
	ElementData['containersData'] & {
		root?: ElementData['containersData']
	}
>

interface UseRenderPropsParams {
	compStructure: AppStructureWithFeatures['components'][string]
	elementToContainersData?: ElementToContainersData
	slots?: Record<string, string>
	parentScope: ScopeData
	propsStore: RendererProps['props']
	displayedItemId: string
	renderComp: RenderComp
	isDs: boolean
	compProps: Record<string, any>
	compId: string
	elementProps?: Record<string, any>
	parentCompPath?: string
	currentCompPath?: string
	experiments: Experiments
}

type ContainerType = 'template' | 'slot' | 'simple' | 'placeholder'
type RenderContainer = React.ReactNode | ((item: unknown, itemIndex: number) => React.ReactNode)
type ContainerTypeRender = (compId?: string) => RenderContainer
type RenderTemplateComp = (templateCompId?: string) => (item: unknown, itemIndex: number) => React.ReactNode

// Stable Provider so TemplateItemContext consumers only re-render when item/index actually change,
// rather than every time the enclosing render function is invoked.
const TemplateItemProvider: React.FC<React.PropsWithChildren<{ item: unknown; index: number }>> = ({
	item,
	index,
	children,
}) => {
	const value = useMemo(() => ({ item, index }), [item, index])
	return React.createElement(TemplateItemContext.Provider, { value }, children)
}

type ContainerProps = {
	rootContainers?: Record<string, RenderContainer>
	elementPropsWithRenderedContainers?: Record<string, Record<string, RenderContainer>>
}

type RenderLayers = {
	children?: Array<React.ReactNode>
	backgroundLayer?: Array<React.ReactNode>
	pinnedLayer?: Array<React.ReactNode>
}
type ItemArrayProps = Record<string, any>

type StructureComponentRenderProps = RenderLayers & ContainerProps & ItemArrayProps

const resolveElementPropsSlotRefsExperiment = 'specs.thunderbolt.resolveElementPropsSlotRefs'

export const useRenderProps = (params: UseRenderPropsParams): StructureComponentRenderProps => {
	const layers = useRenderLayers(params)
	const { elementPropsWithRenderedContainers, rootContainers } = useContainerProps(params)
	const itemArrayProps = useItemArrayProps(params)

	return {
		...layers,
		...rootContainers,
		...itemArrayProps,
		...(elementPropsWithRenderedContainers ? { elementProps: elementPropsWithRenderedContainers } : {}),
	}
}

/**
 * Maps layers to render props (children, backgroundLayer, pinnedLayer)
 */
const useRenderLayers = ({
	compProps,
	propsStore,
	displayedItemId,
	compId,
	parentScope,
	isDs,
	renderComp,
	compStructure,
	slots,
	parentCompPath,
}: UseRenderPropsParams) => {
	return useMemo(() => {
		const components = compStructure?.components ?? []
		const shouldMapLayers = Boolean(
			compStructure?.builderType && components.length > 0 && Object.keys(slots ?? {}).length === 0
		)

		if (!shouldMapLayers) {
			return {}
		}

		const RenderPropsResult: RenderProps = {
			children: [],
			backgroundLayer: [],
			pinnedLayer: [],
		}

		const { layers } = compProps
		const pinnedSet = new Set(layers?.pinned ?? [])
		const backgroundSet = new Set(layers?.background ?? [])

		const renderChild = (childId: string): React.ReactNode => {
			const childScope = isDs ? getChildScope(compId, parentScope) : emptyScope
			return renderComp(propsStore, childId, childScope, displayedItemId, parentCompPath)
		}

		const getLayerKey = (childId: string): keyof RenderProps => {
			if (backgroundSet.has(childId)) {
				return 'backgroundLayer'
			}
			if (pinnedSet.has(childId)) {
				return 'pinnedLayer'
			}
			return 'children'
		}

		return (components ?? []).reduce((acc, childId) => {
			const layer = getLayerKey(childId)
			acc[layer].push(renderChild(childId))
			return acc
		}, RenderPropsResult)
	}, [
		compStructure,
		slots,
		compProps,
		isDs,
		compId,
		parentScope,
		renderComp,
		propsStore,
		displayedItemId,
		parentCompPath,
	])
}

const useRenderSlot = ({
	parentScope,
	propsStore,
	displayedItemId,
	renderComp,
	parentCompPath,
}: UseRenderPropsParams) =>
	useCallback(
		(slotCompId?: string) => {
			if (!slotCompId) {
				return null
			}

			const firstScopeItem = parentScope.scope[0]
			const slotCompIdForScopeGetter = firstScopeItem ? firstScopeItem + REF_DELIMITER + slotCompId : slotCompId
			const slotsScope = getSlotsScope(parentScope, slotCompIdForScopeGetter)

			return renderComp(propsStore, slotCompId, slotsScope, displayedItemId, parentCompPath)
		},
		[parentScope, propsStore, displayedItemId, renderComp, parentCompPath]
	)

// Template container render function for repeater components.
// Per the templateContainer API, the repeater receives this as a prop and calls it
// for each item: items.map((item, index) => renderItem(item, index))
// The item is propagated down the subtree via React Context (TemplateItemContext)
// so ContextBindingWrapper on descendant components can resolve TemplateItem bindings.
const useRenderTemplate = ({
	parentScope,
	propsStore,
	displayedItemId,
	renderComp,
	isDs,
	parentCompPath,
}: UseRenderPropsParams): RenderTemplateComp =>
	useCallback(
		(templateCompId?: string) =>
			(item: unknown, itemIndex: number): React.ReactNode => {
				if (!templateCompId) {
					return null
				}

				// Support both new API (item is an object) and legacy (item is a string itemId)
				const itemId = typeof item === 'string' ? item : String(itemIndex)
				const inflatedItemId = getInflatedItemId(itemId, displayedItemId)
				const childScope = isDs
					? getChildScope(templateCompId, parentScope, {
							parentType: 'Repeater',
							scopeId: inflatedItemId,
							itemIndex,
						})
					: emptyScope

				const renderedTemplate = renderComp(propsStore, templateCompId, childScope, inflatedItemId, parentCompPath)

				// Only object items need to propagate through context (legacy string itemIds
				// don't carry bindable data). When item is not an object, skip the provider
				// so the template renders as-is with no template bindings resolvable.
				if (!item || typeof item !== 'object') {
					return renderedTemplate
				}

				return React.createElement(TemplateItemProvider, { item, index: itemIndex }, renderedTemplate)
			},
		[displayedItemId, isDs, parentScope, propsStore, renderComp, parentCompPath]
	)

const useContainerProps = (params: UseRenderPropsParams): ContainerProps => {
	const { compStructure, elementToContainersData, slots, elementProps, currentCompPath, experiments } = params
	const renderSlotComp = useRenderSlot(params)
	const renderTemplateComp = useRenderTemplate(params)

	return useMemo(() => {
		if (!compStructure?.builderType) {
			return {}
		}

		const containerRenderers: Record<ContainerType, ContainerTypeRender> = {
			template: (compId?: string) => renderTemplateComp(compId),
			slot: (compId?: string) => renderSlotComp(compId),
			simple: (compId?: string) => renderSlotComp(compId),
			placeholder: (compId?: string) => renderSlotComp(compId),
		}

		const resolvedElementProps = resolveInnerElementsPropsContainers(elementProps ?? {}, containerRenderers)
		const resolvedElementPropsWithSlots = experiments[resolveElementPropsSlotRefsExperiment]
			? resolveElementPropsSlotRefs(resolvedElementProps, renderSlotComp)
			: resolvedElementProps

		return {
			rootContainers: resolveRootContainers(containerRenderers, slots, elementToContainersData),
			elementPropsWithRenderedContainers: currentCompPath
				? injectCompPathsIntoElementProps(resolvedElementPropsWithSlots, currentCompPath)
				: resolvedElementPropsWithSlots,
		}
	}, [
		compStructure,
		elementToContainersData,
		renderSlotComp,
		renderTemplateComp,
		elementProps,
		slots,
		currentCompPath,
		experiments,
	])
}

function makeSlotRefProcessor(
	slotMap: Record<string, string>,
	renderSlotComp: (compId?: string) => RenderContainer
): (value: any) => any {
	const processValue = (value: any): any => {
		if (typeof value === 'string' && slotMap[value]) {
			return renderSlotComp(slotMap[value])
		}
		if (Array.isArray(value)) {
			return value.map(processValue)
		}
		if (isPlainObject(value)) {
			return mapValues(value, processValue)
		}
		return value
	}
	return processValue
}

const useItemArrayProps = (params: UseRenderPropsParams) => {
	const renderSlotComp = useRenderSlot(params)
	const renderTemplateComp = useRenderTemplate(params)
	const { compProps, slots, compStructure, experiments, elementToContainersData } = params

	const mapItemArrayProps = useCallback(
		(props: Record<string, any>) => {
			// A top-level key in compProps may itself reference a slot (its value is a UUID
			// resolved through `slots`). When that key is declared as a `template` root
			// container, the consumer (e.g. Repeater) expects a `(item, index) => ReactNode`
			// renderer, not a plain ReactNode — so for those keys we feed the slot-ref
			// processor a template renderer instead of the regular slot renderer.
			const isTemplateContainer = (key: string) => elementToContainersData?.root?.[key]?.containerType === 'template'

			const getRendererForKey = (key: string): ((compId?: string) => RenderContainer) =>
				isTemplateContainer(key) ? renderTemplateComp : renderSlotComp

			if (experiments[resolveElementPropsSlotRefsExperiment]) {
				return mapValues(props, (value, key) => {
					const processValue = makeSlotRefProcessor(slots ?? {}, getRendererForKey(key))
					return processValue(value)
				})
			}

			return mapValues(props, (value, key) => {
				const render = getRendererForKey(key)
				const processValue = (val: any): any => {
					if (typeof val === 'string' && slots?.[val]) {
						return render(slots[val])
					}

					if (Array.isArray(val)) {
						return val.map(processValue)
					}

					if (typeof val === 'object' && val !== null) {
						return Object.entries(val).reduce<any>((acc, [k, v]) => {
							acc[k] = processValue(v)
							return acc
						}, {})
					}

					return val
				}
				return processValue(value)
			})
		},
		[renderSlotComp, renderTemplateComp, slots, experiments, elementToContainersData]
	)

	return useMemo(() => {
		if (!compStructure?.builderType) {
			return {}
		}
		return mapItemArrayProps(compProps)
	}, [mapItemArrayProps, compProps, compStructure])
}

function resolveRootContainers(
	containerRenderers: Record<ContainerType, ContainerTypeRender>,
	slots: Record<string, string> | undefined,
	elementToContainersData?: ElementToContainersData
) {
	const rootContainersData = elementToContainersData?.root ?? {}
	return mapValues(rootContainersData, (containerData, containerName) => {
		const renderer = containerRenderers[containerData.containerType as ContainerType]
		return renderer(slots?.[containerName])
	})
}

function injectCompPathsIntoElementProps(elementProps: Record<string, any>, basePath: string): Record<string, any> {
	return mapValues(elementProps, (element, nickname) => {
		const nicknamePath = `${basePath}${COMP_PATH_DELIMITER}${nickname}`
		element.wix = { ...(element.wix ?? {}), compPath: nicknamePath }
		if (element.elementProps) {
			element.elementProps = injectCompPathsIntoElementProps(element.elementProps, nicknamePath)
		}
		return element
	})
}

function resolveInnerElementsPropsContainers(
	elementProps: Record<string, any>,
	containerRenderers: Record<ContainerType, ContainerTypeRender>
): Record<string, any> {
	const elementPropsWithRenderedContainers = cloneDeep(elementProps ?? {})

	return mapValues(elementPropsWithRenderedContainers, (element, key) => {
		return mapValues(element, (elementData, key) => {
			const isContainer = isPlainObject(elementData) && elementData.containerType
			if (isContainer) {
				const renderer = containerRenderers[elementData.containerType as ContainerType]
				return renderer(elementData.compId)
			}

			if (key === 'elementProps') {
				return resolveInnerElementsPropsContainers(elementData, containerRenderers)
			}

			return elementData
		})
	})
}

// resolveElementSlots may produce {compId} entries without containerType (when the manifest
// doesn't declare containersData for dynamic slots, e.g. Menu item containers).
// resolveInnerElementsPropsContainers skips those since it checks for containerType.
// This picks them up and resolves any matching UUID string refs in nested data (like items[].container).
function resolveElementPropsSlotRefs(
	elementProps: Record<string, any>,
	renderSlotComp: (compId?: string) => React.ReactNode
): Record<string, any> {
	return mapValues(elementProps, (element) => {
		const localSlots: Record<string, string> = {}
		for (const [key, val] of Object.entries(element) as Array<[string, any]>) {
			if (isPlainObject(val) && typeof val.compId === 'string' && !val.containerType) {
				localSlots[key] = val.compId
			}
		}

		if (Object.keys(localSlots).length === 0) {
			return element
		}

		const processValue = makeSlotRefProcessor(localSlots, renderSlotComp)
		return mapValues(element, (val, key) => {
			if (key in localSlots) {
				return val
			}

			if (key === 'elementProps') {
				return resolveElementPropsSlotRefs(val, renderSlotComp)
			}

			return processValue(val)
		})
	})
}
